import { useState, useCallback } from 'react';
import { useApi } from '../../../shared/hooks/useApi';
import { userAPI } from '../../../shared/services/api';
import { User, PaginatedResponse } from '../../../shared/types';

export function useUserSearch() {
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  const {
    execute: performSearch,
    isLoading,
    error
  } = useApi<PaginatedResponse<User>>(userAPI.searchUsers);
  
  const searchUsers = useCallback(async (query: string, page: number = 0, size: number = 20) => {
    if (!query.trim()) {
      setSearchResults([]);
      setTotalResults(0);
      setHasSearched(false);
      return;
    }
    
    const result = await performSearch(query, page, size);
    if (result) {
      setSearchResults(result.content);
      setTotalResults(result.totalElements);
      setCurrentPage(result.currentPage);
      setHasSearched(true);
    }
    return result;
  }, [performSearch]);
  
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setTotalResults(0);
    setCurrentPage(0);
    setHasSearched(false);
  }, []);
  
  const loadMore = useCallback(async (query: string, size: number = 20) => {
    const nextPage = currentPage + 1;
    const result = await performSearch(query, nextPage, size);
    if (result) {
      setSearchResults(prev => [...prev, ...result.content]);
      setCurrentPage(result.currentPage);
    }
    return result;
  }, [performSearch, currentPage]);
  
  return {
    searchResults,
    totalResults,
    currentPage,
    hasSearched,
    isLoading,
    error,
    searchUsers,
    clearSearch,
    loadMore
  };
}